import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAppSelector } from '../../hooks/useAppSelector';
import { API_ENDPOINTS } from '../../config/api';
import { useToast } from '../../hooks/useToast';
import Toast from '../../components/Toast';

const RestaurantProfile = () => {
    const { user, restaurant } = useAppSelector(state => state.auth);
    // Use restaurant._id if available, otherwise fall back to user.id
    const restaurantId = restaurant?._id || user?.restaurantId || user?.id;
    const { toasts, showToast, removeToast } = useToast();
    
    const [profileData, setProfileData] = useState({
        name: '',
        email: '',
        phoneNumber: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        cuisine: '',
        description: '',
        logo: '',
        taxPercentage: '18', // Store as string to preserve exact decimal values
        openingTime: '',
        closingTime: '',
        isActive: true,
        password: '' // Password field (empty for security)
    });
    
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchRestaurantProfile = async () => {
            if (!restaurantId) {
                console.error('No restaurant ID available');
                showToast('Restaurant ID not found. Please log in again.', 'error');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await axios.get(API_ENDPOINTS.RESTAURANT_BY_ID(restaurantId));
                const data = response.data;
                
                // Map phone field for backward compatibility
                setProfileData({
                    ...data,
                    phoneNumber: data.phoneNumber || data.phone || '',
                    taxPercentage: data.taxPercentage !== undefined && data.taxPercentage !== null ? String(data.taxPercentage) : '18',
                    isActive: data.isActive !== undefined ? data.isActive : data.active,
                    // Don't include password in initial state for security (password field will be empty)
                    password: ''
                });
            } catch (error) {
                console.error('Failed to fetch restaurant profile:', error);
                showToast('Failed to load restaurant profile', 'error');
            } finally {
                setLoading(false);
            }
        };

        if (restaurantId) {
            fetchRestaurantProfile();
        }
    }, [restaurantId]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let processedValue = value;
        
        // Handle number inputs properly (taxPercentage, etc.)
        if (type === 'number' && name === 'taxPercentage') {
            // For taxPercentage, keep as string to preserve exact decimal values
            // This prevents floating-point precision issues (e.g., 5.00 won't become 5.02)
            if (value === '' || value === null || value === undefined) {
                processedValue = '';
            } else {
                // Validate that it's a valid number format
                // Allow empty, numbers, and partial decimals (like "18.")
                const isValidNumber = /^-?\d*\.?\d*$/.test(value);
                if (isValidNumber || value === '') {
                    // Store as string to preserve exact value
                    processedValue = value;
                } else {
                    // Invalid input, don't update
                    return;
                }
            }
        } else if (type === 'number') {
            // For other number inputs, use standard handling
            if (value === '' || value === null || value === undefined) {
                processedValue = '';
            } else {
                const numValue = parseFloat(value);
                if (!isNaN(numValue) || value.endsWith('.') || value === '') {
                    processedValue = value;
                } else {
                    return;
                }
            }
        }
        
        setProfileData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : processedValue
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            // Convert taxPercentage from string to number properly
            // Use Number() instead of parseFloat() to preserve exact decimal values
            let taxPercentageValue = 0;
            if (profileData.taxPercentage !== '' && profileData.taxPercentage != null) {
                const taxStr = String(profileData.taxPercentage).trim();
                if (taxStr !== '') {
                    // Convert string to number, preserving decimal precision
                    taxPercentageValue = Number(taxStr);
                    // Validate it's a valid number and within range
                    if (isNaN(taxPercentageValue)) {
                        taxPercentageValue = 0;
                    } else {
                        // Clamp between 0 and 100
                        taxPercentageValue = Math.max(0, Math.min(100, taxPercentageValue));
                    }
                }
            }
            
            const dataToSave = {
                ...profileData,
                taxPercentage: taxPercentageValue
            };
            
            // Only include password if it's been changed (not empty)
            // If password field is empty, don't send it (backend will keep existing password)
            if (!dataToSave.password || dataToSave.password.trim() === '') {
                delete dataToSave.password;
            }
            
            await axios.put(API_ENDPOINTS.RESTAURANT_UPDATE(restaurantId), dataToSave);
            showToast('Restaurant profile updated successfully!', 'success');
            setIsEditing(false);
        } catch (error) {
            console.error('Failed to update restaurant profile:', error);
            showToast('Failed to update restaurant profile', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = async () => {
        setIsEditing(false);
        // Reset to original data
        try {
            const response = await axios.get(API_ENDPOINTS.RESTAURANT_BY_ID(restaurantId));
            const data = response.data;
            setProfileData({
                ...data,
                phoneNumber: data.phoneNumber || data.phone || '',
                taxPercentage: data.taxPercentage !== undefined && data.taxPercentage !== null ? String(data.taxPercentage) : '18',
                isActive: data.isActive !== undefined ? data.isActive : data.active,
                // Don't include password in reset state for security (password field will be empty)
                password: ''
            });
        } catch (error) {
            console.error('Failed to fetch restaurant profile:', error);
        }
    };

    const inputBase = 'w-full px-3 py-2.5 text-sm border border-stone-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none transition-shadow disabled:bg-stone-100 disabled:text-stone-500 hover:border-stone-300';
    const labelBase = 'block text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500 mb-1.5';

    if (loading) {
        return (
            <div className="min-h-screen bg-stone-100 w-full px-0 sm:px-4">
                <div className="w-full">
                    <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 sm:p-8 animate-pulse overflow-hidden">
                        <div className="h-8 bg-gradient-to-r from-stone-200 to-stone-100 rounded-lg w-1/3 mb-8" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="space-y-3">
                                    <div className="h-4 bg-stone-100 rounded w-1/4" />
                                    {[1, 2, 3, 4].map(j => (
                                        <div key={j} className="h-12 bg-stone-50 rounded-xl" />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-stone-100 w-full px-0 sm:px-4">
            <div className="w-full">
                <div className="bg-white rounded-2xl border border-stone-200 shadow-md overflow-hidden ring-1 ring-stone-900/5">
                    {/* Header */}
                    <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6 sm:py-4 border-b border-stone-200 bg-gradient-to-r from-stone-50 via-orange-50/50 to-stone-50 backdrop-blur">
                        <div className="flex items-center gap-4">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25">
                                <span className="text-xl" aria-hidden>🏪</span>
                            </div>
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">Restaurant profile</p>
                                <p className="mt-0.5 text-base font-semibold text-stone-800">Manage your restaurant information</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {!isEditing ? (
                                <button
                                    type="button"
                                    onClick={() => setIsEditing(true)}
                                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-orange-600 hover:shadow-lg transition-all"
                                >
                                    Edit profile
                                </button>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={handleSave}
                                        disabled={saving}
                                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-orange-600 hover:shadow-lg disabled:opacity-50 transition-all"
                                    >
                                        {saving ? 'Saving…' : 'Save'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-stone-200 bg-white px-5 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50 hover:border-stone-300 transition-all"
                                    >
                                        Cancel
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Profile content */}
                    <div className="p-4 sm:p-6 lg:p-8">
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8">
                            {/* Basic information */}
                            <div className="space-y-6">
                                <div className="relative overflow-hidden rounded-xl border border-stone-200 bg-gradient-to-br from-white to-stone-50/80 p-5 sm:p-6 shadow-sm">
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-400 to-orange-500 rounded-l-xl" />
                                    <div className="flex items-center gap-2 mb-4 pl-1">
                                        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-orange-100 text-orange-600 text-sm">📋</span>
                                        <p className={labelBase + ' mb-0'}>Basic information</p>
                                    </div>
                                    <div className="space-y-4 mt-4 pl-1">
                                        <div>
                                            <label className={labelBase}>Restaurant name</label>
                                            <input type="text" name="name" value={profileData.name} onChange={handleInputChange} disabled={!isEditing} className={inputBase} />
                                        </div>
                                        <div>
                                            <label className={labelBase}>Email</label>
                                            <input type="email" name="email" value={profileData.email} onChange={handleInputChange} disabled className={inputBase} />
                                        </div>
                                        <div>
                                            <label className={labelBase}>Phone number</label>
                                            <input type="tel" name="phoneNumber" value={profileData.phoneNumber} onChange={handleInputChange} disabled={!isEditing} className={inputBase} />
                                        </div>
                                        <div>
                                            <label className={labelBase}>Cuisine type</label>
                                            <input type="text" name="cuisine" value={profileData.cuisine} onChange={handleInputChange} disabled={!isEditing} placeholder="e.g. Italian, Indian" className={inputBase} />
                                        </div>
                                    </div>
                                </div>

                                {/* Business settings */}
                                <div className="relative overflow-hidden rounded-xl border border-stone-200 bg-gradient-to-br from-white to-emerald-50/30 p-5 sm:p-6 shadow-sm">
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-emerald-400 to-emerald-500 rounded-l-xl" />
                                    <div className="flex items-center gap-2 mb-4 pl-1">
                                        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 text-sm">⚙️</span>
                                        <p className={labelBase + ' mb-0'}>Business settings</p>
                                    </div>
                                    <div className="space-y-4 mt-4 pl-1">
                                        <div>
                                            <label className={labelBase}>Tax (%)</label>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                name="taxPercentage"
                                                value={profileData.taxPercentage !== undefined && profileData.taxPercentage !== null ? String(profileData.taxPercentage) : ''}
                                                onChange={handleInputChange}
                                                onBlur={(e) => {
                                                    const value = e.target.value.trim();
                                                    if (value !== '') {
                                                        const numValue = Number(value);
                                                        if (!isNaN(numValue)) {
                                                            const clampedValue = Math.max(0, Math.min(100, numValue));
                                                            const decimalPlaces = value.includes('.') ? value.split('.')[1]?.length || 0 : 0;
                                                            const formattedValue = decimalPlaces > 0 ? clampedValue.toFixed(Math.min(decimalPlaces, 2)) : String(clampedValue);
                                                            setProfileData(prev => ({ ...prev, taxPercentage: formattedValue }));
                                                        } else {
                                                            setProfileData(prev => ({ ...prev, taxPercentage: '' }));
                                                        }
                                                    }
                                                }}
                                                disabled={!isEditing}
                                                min="0"
                                                max="100"
                                                step="0.01"
                                                pattern="[0-9]*\.?[0-9]*"
                                                className={inputBase}
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className={labelBase}>Opening time</label>
                                                <input type="time" name="openingTime" value={profileData.openingTime} onChange={handleInputChange} disabled={!isEditing} className={inputBase} />
                                            </div>
                                            <div>
                                                <label className={labelBase}>Closing time</label>
                                                <input type="time" name="closingTime" value={profileData.closingTime} onChange={handleInputChange} disabled={!isEditing} className={inputBase} />
                                            </div>
                                        </div>
                                        <div className={`flex items-center gap-3 pt-2 px-3 py-2.5 rounded-xl border ${profileData.isActive ? 'bg-emerald-50/80 border-emerald-200' : 'bg-stone-50 border-stone-200'}`}>
                                            <input type="checkbox" name="isActive" checked={profileData.isActive} onChange={handleInputChange} disabled={!isEditing} className="h-4 w-4 rounded border-stone-300 text-orange-500 focus:ring-orange-400 disabled:opacity-50" />
                                            <label className="text-xs font-semibold text-stone-700">Restaurant is currently active</label>
                                            {profileData.isActive && <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-emerald-600">On</span>}
                                        </div>
                                    </div>
                                </div>

                                {/* Security */}
                                <div className="relative overflow-hidden rounded-xl border border-stone-200 bg-gradient-to-br from-white to-amber-50/20 p-5 sm:p-6 shadow-sm">
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-400 to-amber-500 rounded-l-xl" />
                                    <div className="flex items-center gap-2 mb-4 pl-1">
                                        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-100 text-amber-600 text-sm">🔒</span>
                                        <p className={labelBase + ' mb-0'}>Security</p>
                                    </div>
                                    <div className="mt-4 pl-1">
                                        <label className={labelBase}>New password</label>
                                        <input type="password" name="password" value={profileData.password} onChange={handleInputChange} disabled={!isEditing} placeholder="Leave blank to keep current" className={inputBase} />
                                        <p className="text-[10px] text-stone-500 mt-1.5">Leave blank to keep your current password</p>
                                    </div>
                                </div>
                            </div>

                            {/* Address & description */}
                            <div className="space-y-6">
                                <div className="relative overflow-hidden rounded-xl border border-stone-200 bg-gradient-to-br from-white to-sky-50/30 p-5 sm:p-6 shadow-sm">
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-sky-400 to-sky-500 rounded-l-xl" />
                                    <div className="flex items-center gap-2 mb-4 pl-1">
                                        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-sky-100 text-sky-600 text-sm">📍</span>
                                        <p className={labelBase + ' mb-0'}>Address</p>
                                    </div>
                                    <div className="space-y-4 mt-4 pl-1">
                                        <div>
                                            <label className={labelBase}>Full address</label>
                                            <textarea name="address" value={profileData.address} onChange={handleInputChange} disabled={!isEditing} rows={3} className={inputBase + ' resize-none'} />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className={labelBase}>City</label>
                                                <input type="text" name="city" value={profileData.city} onChange={handleInputChange} disabled={!isEditing} className={inputBase} />
                                            </div>
                                            <div>
                                                <label className={labelBase}>State</label>
                                                <input type="text" name="state" value={profileData.state} onChange={handleInputChange} disabled={!isEditing} className={inputBase} />
                                            </div>
                                        </div>
                                        <div>
                                            <label className={labelBase}>Pincode</label>
                                            <input type="text" name="pincode" value={profileData.pincode} onChange={handleInputChange} disabled={!isEditing} className={inputBase} />
                                        </div>
                                    </div>
                                </div>

                                <div className="relative overflow-hidden rounded-xl border border-stone-200 bg-gradient-to-br from-white to-stone-50/80 p-5 sm:p-6 shadow-sm">
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-stone-400 to-stone-500 rounded-l-xl" />
                                    <div className="flex items-center gap-2 mb-4 pl-1">
                                        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-stone-200 text-stone-600 text-sm">📝</span>
                                        <p className={labelBase + ' mb-0'}>Description & logo</p>
                                    </div>
                                    <div className="space-y-4 mt-4 pl-1">
                                        <div>
                                            <label className={labelBase}>Description</label>
                                            <textarea name="description" value={profileData.description} onChange={handleInputChange} disabled={!isEditing} rows={4} placeholder="Tell customers about your restaurant…" className={inputBase + ' resize-none'} />
                                        </div>
                                        <div>
                                            <label className={labelBase}>Logo URL</label>
                                            <input type="url" name="logo" value={profileData.logo} onChange={handleInputChange} disabled={!isEditing} placeholder="https://example.com/logo.png" className={inputBase} />
                                        </div>
                                        {profileData.logo && (
                                            <div className="pt-3">
                                                <p className={labelBase}>Logo preview</p>
                                                <div className="mt-2 inline-flex p-3 rounded-xl bg-stone-100 border border-stone-200">
                                                    <img src={profileData.logo} alt="Restaurant logo" className="h-20 w-20 object-cover rounded-lg border border-stone-200 shadow-inner" onError={(e) => { e.target.style.display = 'none'; }} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Toast Notifications */}
            {toasts.map(toast => (
                <Toast
                    key={toast.id}
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onClose={() => removeToast(toast.id)}
                />
            ))}
        </div>
    );
};

export default RestaurantProfile;